import React, { useState, useMemo, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsSankey from 'highcharts/modules/sankey';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsExportData from 'highcharts/modules/export-data';
import {
    Box,
    ToggleButton,
    ToggleButtonGroup,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    OutlinedInput,
    InputAdornment,
    IconButton,
    Autocomplete
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { advanceSearch } from "../../../../_helper/services/advanceSearchv2";
import { getFormalApiErrorMessage } from "../../../../utils/apiError";

HighchartsSankey(Highcharts);
HighchartsExporting(Highcharts);
HighchartsExportData(Highcharts);

// --- HELPER FUNCTIONS AND CONSTANTS ---
const calculateAge = (dob, asOfDate) => {
    const birthDate = new Date(dob);
    const effectiveDate = new Date(asOfDate);
    if (isNaN(birthDate.getTime()) || isNaN(effectiveDate.getTime())) return null;
    let age = effectiveDate.getFullYear() - birthDate.getFullYear();
    const monthDifference = effectiveDate.getMonth() - birthDate.getMonth();
    if (monthDifference < 0 || (monthDifference === 0 && effectiveDate.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

const getAgeGroup = (age) => {
    if (age >= 18 && age <= 24) return '18-24 GenZ';
    if (age >= 25 && age <= 34) return '25-34 Emerging Pro';
    if (age >= 35 && age <= 44) return '35-44 Established Pro';
    if (age >= 45 && age <= 54) return '45-54 Experienced Pro';
    if (age >= 55 && age <= 64) return '55-64 Later Career';
    if (age >= 65) return '65+ Retirement';
    return 'Under 18 / Other';
};

const ageGroups = [
    '18-24 GenZ', '25-34 Emerging Pro', '35-44 Established Pro',
    '45-54 Experienced Pro', '55-64 Later Career', '65+ Retirement'
];

const GG1CorrAnalys = () => {
    const [chartXData, setXChartData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(9999);
    const [noa, setNoa] = useState([]);
    const [viewBy, setViewBy] = useState('age');

    const getNoa = async () => {


        const payload = {
            tableName: "pmrc_nature",
            searchParams: "[]",
            columns: "*",
            location: "global",
            pageNumber: currentPage,
            pageSize: itemsPerPage,
            sortData: "",
            additionalWhere: '',
            qID: 0,
            sortBy: "asc",
        };

        try {
            const response = await advanceSearch(payload);
            const { results, total } = response;
            setNoa(results);
        } catch (error) {
            console.error(getFormalApiErrorMessage(error, "Something went wrong"));
        }
    };

    useEffect(() => {
        getNoa();
    }, []);

    useEffect(() => {
        if (noa.length > 0) {
            setSelectedSeries(actionTypeOptions);
        }
    }, [noa]);
    const [filters, setFilters] = useState(() => {
        const today = new Date();
        const endDate = today.toISOString().split('T')[0];
        const startDate = new Date(today.getFullYear(), today.getMonth() - 14, today.getDate()).toISOString().split('T')[0];
        return {
            startDate,
            endDate,
            actionTypes: []
        };
    });
    const [selectedSeries, setSelectedSeries] = useState([]);
    const [selectedAgeGroups, setSelectedAgeGroups] = useState(ageGroups);
    const [selectedGenders, setSelectedGenders] = useState(['Males', 'Females', 'Not Specified']);

    const handleViewChange = (event, newView) => {
        if (newView !== null) {
            setViewBy(newView);
        }
    };

    const handleFilterChange = (field, value) => {
        if (field === 'startDate' || field === 'endDate') {
            const newFilters = { ...filters, [field]: value };
            const { startDate, endDate } = newFilters;

            if (startDate && endDate) {
                const start = new Date(startDate);
                const end = new Date(endDate);
                const monthsDiff = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());

                if (monthsDiff >= 37) {
                    alert('Date range must be less than or equal to 36 months');
                    return;
                }
            }
        }
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const actionTypeOptions = noa.map(item => `${item.NATURE_CODE} - ${item.NATURE_TITLE?.trim()}`);

    const getChartDataMultiaxis = async () => {
        let additionalWhere = '';
        if (filters.startDate && filters.endDate) {
            additionalWhere = `DATE_ENTERED BETWEEN '${filters.startDate}' AND '${filters.endDate}'`;
        } else {
            // Default to last 14 months if no filters
            const today = new Date();
            const endDate = today.toISOString().split('T')[0];
            const startDate = new Date(today.getFullYear(), today.getMonth() - 14, today.getDate()).toISOString().split('T')[0];
            additionalWhere = `DATE_ENTERED BETWEEN '${startDate}' AND '${endDate}'`;
        }

        const payload = {
            tableName: "vw_GG1_TRAN_Sankey", searchParams: "[]", columns: "*", location: "global",
            pageNumber: currentPage, pageSize: itemsPerPage, sortData: "", additionalWhere, qID: 0,
        };
        try {
            const response = await advanceSearch(payload);
            setXChartData(response.results);
        } catch (error) {
            console.error(getFormalApiErrorMessage(error, "Something went wrong"));
        }
    };

    useEffect(() => {
        getChartDataMultiaxis();
    }, [currentPage, itemsPerPage, filters.startDate, filters.endDate]);

    const chartOptionsXaxis = useMemo(() => {
        const monthlyData = {};

        // Filter data based on current filters
        const filteredData = chartXData.filter(item => {
            const actionDate = new Date(item.DATE_ENTERED);

            // Date range filter
            if (filters.startDate && actionDate < new Date(filters.startDate)) return false;
            if (filters.endDate && actionDate > new Date(filters.endDate)) return false;

            // Action type filter
            if (filters.actionTypes.length > 0) {
                const itemActionType = `${item.NATURE_CODE} - ${item.NatureOfActionTitle?.trim()}`;
                if (!filters.actionTypes.includes(itemActionType)) return false;
            }



            return true;
        });

        // 1. Data processing
        const actionTypeData = {};
        actionTypeOptions.forEach(actionType => {
            actionTypeData[actionType] = {};
        });

        filteredData.forEach(item => {
            const date = new Date(item.DATE_ENTERED);
            const month = date.toLocaleString('default', { month: 'long', year: 'numeric' });
            const actionType = `${item.NATURE_CODE} - ${item.NatureOfActionTitle?.trim()}`;

            if (!monthlyData[month]) {
                monthlyData[month] = { males: 0, females: 0, notSpecified: 0 };
                ageGroups.forEach(group => monthlyData[month][group] = 0);
                actionTypeOptions.forEach(type => monthlyData[month][type] = 0);
            }

            if (actionTypeOptions.includes(actionType)) {
                monthlyData[month][actionType]++;
            }

            if (item.Sex === 'M') monthlyData[month].males++;
            else if (item.Sex === 'F') monthlyData[month].females++;
            else monthlyData[month].notSpecified++;
            if (item.Ndate_Of_Birth) {
                const age = calculateAge(item.Ndate_Of_Birth, item.DATE_ENTERED);
                if (age !== null) {
                    const group = getAgeGroup(age);
                    if (monthlyData[month].hasOwnProperty(group)) {
                        monthlyData[month][group]++;
                    }
                }
            }
        });

        const allMonthsSorted = Object.keys(monthlyData).sort((a, b) => new Date(a) - new Date(b));
        const categories = allMonthsSorted.slice(-14);

        // 2. Data arrays preparation
        const males = categories.map(month => monthlyData[month].males || 0);
        const females = categories.map(month => monthlyData[month].females || 0);
        const ageGroupSeriesData = {};
        ageGroups.forEach(group => {
            ageGroupSeriesData[group] = categories.map(month => monthlyData[month][group] || 0);
        });

        let demographicSeries = [];
        if (viewBy === 'age') {
            demographicSeries = selectedAgeGroups.map(group => ({
                name: group, type: 'spline', yAxis: 1, data: ageGroupSeriesData[group],
            }));
        } else {
            const genderSeriesData = {
                'Males': males,
                'Females': females,
                'Not Specified': categories.map(month => monthlyData[month].notSpecified || 0)
            };
            demographicSeries = selectedGenders.map(gender => ({
                name: gender,
                type: 'spline',
                yAxis: 1,
                data: genderSeriesData[gender] || []
            }));
        }

        // --- NEW: Y-axis ka title dynamically set karna ---
        const secondaryAxisTitle = viewBy === 'age' ? 'Age Group Count' : 'Gender Count';

        // 3. Highcharts configuration
        return {
            chart: { zoomType: 'xy', panning: true, panKey: 'shift', height: 900 },
            title: { text: 'GG1 Correlation Analysis (Last 14 Months)', align: 'left' },
            exporting: {
                enabled: true,
                buttons: {
                    contextButton: {
                        menuItems: ['viewFullscreen', 'printChart', 'separator', 'downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG', 'separator', 'downloadCSV', 'downloadXLS', 'viewData']
                    }
                },
                tableDecimalPoint: '.',
                useMultiLevelHeaders: false,
                useRowspanHeaders: false,
                csv: {
                    columnHeaderFormatter: function (item, key) {
                        if (!item || item.constructor === Highcharts.Axis) {
                            return 'Category';
                        } else {
                            return item.name;
                        }
                    }
                }
            },
            lang: {
                viewData: 'View data table',
                hideData: 'Hide data table'
            },
            xAxis: [{ categories: categories, crosshair: true }],
            yAxis: [
                { // Primary Y-Axis (Left)
                    title: { text: 'Number of Actions' },
                    labels: { format: '{value}' }
                },
                { // Secondary Y-Axis (Right)
                    // --- MODIFIED: Dynamic title yahan use hoga ---
                    title: { text: secondaryAxisTitle },
                    labels: { format: '{value}' },
                    opposite: true
                }
            ],
            tooltip: { shared: true },
            legend: { align: 'center', verticalAlign: 'bottom', floating: false },
            series: [
                ...selectedSeries.map(actionType => ({
                    name: actionType,
                    type: 'spline',
                    yAxis: 0,
                    data: categories.map(month => monthlyData[month][actionType] || 0)
                })),
                ...demographicSeries
            ]
        };
    }, [chartXData, viewBy, filters, selectedSeries, selectedAgeGroups, selectedGenders]);

    return (
        <Box sx={{ padding: 2 }}>
            {/* Filter Controls */}
            <Box sx={{ marginBottom: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    label="Start Date"
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    inputProps={{
                        min: new Date(new Date().getFullYear(), new Date().getMonth() - 36, new Date().getDate()).toISOString().split('T')[0],
                        max: new Date().toISOString().split('T')[0]
                    }}
                />
                <TextField
                    label="End Date"
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    inputProps={{
                        min: new Date(new Date().getFullYear(), new Date().getMonth() - 36, new Date().getDate()).toISOString().split('T')[0],
                        max: new Date().toISOString().split('T')[0]
                    }}
                />
                <Autocomplete
                    multiple
                    size="small"
                    sx={{ minWidth: 200 }}
                    options={actionTypeOptions}
                    value={selectedSeries}
                    onChange={(event, newValue) => setSelectedSeries(newValue)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Action Types"
                            placeholder="Search action types..."
                        />
                    )}
                />
                {viewBy === 'age' && (
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Age Groups</InputLabel>
                        <Select
                            multiple
                            value={selectedAgeGroups}
                            onChange={(e) => setSelectedAgeGroups(e.target.value)}
                            input={<OutlinedInput label="Age Groups" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip key={value} label={value} size="small" />
                                    ))}
                                </Box>
                            )}
                        >
                            {ageGroups.map((group) => (
                                <MenuItem key={group} value={group}>{group}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                {viewBy === 'gender' && (
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Gender Groups</InputLabel>
                        <Select
                            multiple
                            value={selectedGenders}
                            onChange={(e) => setSelectedGenders(e.target.value)}
                            input={<OutlinedInput label="Gender Groups" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip key={value} label={value} size="small" />
                                    ))}
                                </Box>
                            )}
                        >
                            {['Males', 'Females', 'Not Specified'].map((gender) => (
                                <MenuItem key={gender} value={gender}>{gender}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Box>

            {/* View Toggle */}
            <Box sx={{ marginBottom: 2, display: 'flex', justifyContent: 'center' }}>
                <ToggleButtonGroup
                    color="primary" value={viewBy} exclusive
                    onChange={handleViewChange} aria-label="Analysis View"
                >
                    <ToggleButton value="age">View by Age Group</ToggleButton>
                    <ToggleButton value="gender">View by Gender</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <HighchartsReact highcharts={Highcharts} options={chartOptionsXaxis} />
        </Box>
    );
};

export default GG1CorrAnalys;